import * as React from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Toast as ToastType, ToastType as ToastVariant } from '@/contexts/ToastContext';

type SwissVariant = 'default' | 'destructive' | 'success';

const variantMap: Record<ToastVariant, SwissVariant> = {
  success: 'success',
  error: 'destructive',
  info: 'default',
  warning: 'default',
};

// Swiss-minimal toasts: inverted dropdown treatment — solid black fill, light
// text, colored left-edge accent + accent-colored icon. Accent reds/greens
// brighter than the light-mode tokens so they read legibly on dark.
const variantStyles: Record<SwissVariant, string> = {
  default: 'bg-foreground text-background',
  destructive: 'bg-foreground text-background',
  success: 'bg-foreground text-background',
};

const variantIcons: Record<SwissVariant, React.ReactNode> = {
  default: null,
  destructive: <AlertCircle className="h-5 w-5 stroke-[1.5] text-red-400" />,
  success: <CheckCircle className="h-5 w-5 stroke-[1.5] text-green-400" />,
};

const variantTitleStyles: Record<SwissVariant, string> = {
  default: 'text-background',
  destructive: 'text-red-400',
  success: 'text-green-400',
};

interface ToastProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

export function Toast({ toast, onDismiss }: ToastProps) {
  const hasTitle = !!toast.title;
  const variant = variantMap[toast.type];
  const icon = variantIcons[variant];

  return (
    <div
      className={cn(
        'pointer-events-auto relative flex w-full gap-3 rounded-2xl p-4 pb-5',
        hasTitle ? 'items-start' : 'items-center',
        variantStyles[variant]
      )}
      role="alert"
      aria-live="polite"
    >
      {icon && <div className="flex-shrink-0 flex items-center">{icon}</div>}
      <div className="flex-1 space-y-1">
        {toast.title && (
          <p className={cn('text-base font-medium', variantTitleStyles[variant])}>{toast.title}</p>
        )}
        <p className="text-sm text-background/70">{toast.message}</p>
      </div>
      <button
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 cursor-pointer text-background/60 hover:text-background focus:outline-none focus:ring-2 focus:ring-background/30 focus:ring-offset-2 focus:ring-offset-foreground"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4 stroke-[1.5]" />
      </button>
    </div>
  );
}
